import type {
    Absence,
    AbsenceType,
    Availability, AvailabilityCoordinate, AvailabilityCoordinateInput, AvailabilityFragment,
    AvailabilityShiftInput,
    AvailabilityTimeConstraintInput,
    Day,
} from "@/graphql/generated/graphql";

import { mutation } from "@/graphql/client";
import { gql } from "@urql/vue";
import { kapitelDateString } from "@/graphql/kapitelTypes";
import useEmitter, {useGlobalEmitter} from "@/helper/emitter";
import {graphql} from "@/graphql/generated";
import {jobsiteFragment} from "@/helper/jobsite";

export async function setAvailabilityAbsence(
    availability: Availability | null,
    absence: Absence | null
): Promise<Day> {

    const MUTATION = graphql(`
        mutation setAvailabilityAbsence(
            $dateDings: KapitelDateImmutable!
            $shiftsDings: [AvailabilityShiftInput!]
            $constraintsDings: [AvailabilityTimeConstraintInput!]
            $excludedJobsiteIdsDings: [Int!]
            $additionalCoordinatesDings: [AvailabilityCoordinateInput!]
            $typeDings: AbsenceType
            $worktimeValuationDings: Float
            $commentDings: String
        ) {
            setAvailabilityAbsence(
                date: $dateDings
                availabilityShifts: $shiftsDings
                availabilityConstraints: $constraintsDings
                availabilityExcludedJobsiteIds: $excludedJobsiteIdsDings
                availabilityAdditionalCoordinates: $additionalCoordinatesDings
                absenceType: $typeDings
                absenceWorktimeValuation: $worktimeValuationDings
                absenceComment: $commentDings
            ){
                ...Day
            }
        }
    `);

    // Availability vars
    let shiftsDings: Array<AvailabilityShiftInput> | null = null;
    let constraintsDings: Array<AvailabilityTimeConstraintInput> | null = null;
    let excludedJobsiteIdsDings: Array<number> | undefined | null = null;
    let additionalCoordinatesDings: Array<AvailabilityCoordinateInput> | null = null;

    // Absence vars
    let typeDings: AbsenceType | null = null;
    let worktimeValuationDings: number | undefined | null = null;
    let commentDings: string | undefined | null = null;

    let dateDings: kapitelDateString|undefined = undefined

    if (availability) {
        dateDings = availability.date
        shiftsDings = availability.shifts
        constraintsDings = availability.constraints
        excludedJobsiteIdsDings = availability.excludedJobsites
            ? availability.excludedJobsites.map((j) => j.id)
            : undefined;
        const makeACInputType = (i: AvailabilityCoordinate): AvailabilityCoordinateInput => {
            return {
                begin: i.begin,
                until: i.until,
                jobsite: {
                    id: i.jobsite.id
                }
            } as AvailabilityCoordinateInput;
        }
        additionalCoordinatesDings = availability.additionalAvailabilityCoordinates.map(makeACInputType)
    }

    if (absence) {
        dateDings = absence.date
        typeDings = absence.type
        worktimeValuationDings = absence.worktimeValuation
        commentDings = absence.comment
    }

    const result = await mutation(MUTATION, {
        dateDings: dateDings,
        shiftsDings: shiftsDings,
        constraintsDings: constraintsDings,
        excludedJobsiteIdsDings: excludedJobsiteIdsDings,
        additionalCoordinatesDings: additionalCoordinatesDings,
        typeDings: typeDings,
        worktimeValuationDings: worktimeValuationDings,
        commentDings: commentDings,
    });

    useGlobalEmitter().emit('day:mutated', dateDings as kapitelDateString)

    const success = result?.data?.setAvailabilityAbsence;

    if (!success) {
        throw new Error("error while storing availability and absence");
    }

    return success;
}
